// @ts-expect-error
import * as filestack from "filestack-js/build/browser/filestack.esm";
import type { ConfigStore } from "../../stores/config.store";
import type { SharedFileInput } from "./files";

export const getFilestackClient = async (
  config: ConfigStore,
): Promise<filestack.Client> => {
  const security = await getFileStackSecurity(config);

  // sessionCache matches the main frontend: cloud auth is kept in localStorage,
  // so an already-connected OneDrive/Google Drive/Dropbox is reused instead of re-authorizing.
  return createFilestackClient(
    { security, sessionCache: true },
    config.workspace.settings.filestack_api_key,
  );
};

export const storeSharedFiles = async (
  config: ConfigStore,
  files: SharedFileInput[],
): Promise<SharedFileInput[]> => {
  const result = await fetch(
    `${config.smartsuiteBackendUrl}/shared-files/bulk_create/`,
    {
      method: "POST",
      headers: {
        ...buildAuthHeader(config),
        "content-type": "application/json",
      },
      body: JSON.stringify(files),
    },
  );
  return await result.json();
};

const getFileStackSecurity = async (
  config: ConfigStore,
): Promise<filestack.Security> => {
  const result = await fetch(
    `${config.smartsuiteBackendUrl}/shared-files/store_security/`,
    {
      headers: buildAuthHeader(config),
    },
  );
  return await result.json();
};

const buildAuthHeader = (config: ConfigStore): HeadersInit => ({
  "account-id": config.accountId,
  // accessToken is empty - means - we are part of monolith, and cookies already contains access token,
  // so we skip Authorization header.
  ...(config.accessToken !== "" && {
    Authorization: `Bearer ${config.accessToken}`,
  }),
});

/**
 * Create a filestack client with environment variables and default configuration plus additional given options.
 */
const createFilestackClient = (
  options: filestack.ClientOptions = {},
  apiKey: string,
): filestack.Client => {
  // Empty CNAME is valid: like the main frontend, only production routes Filestack through files.smartsuite.com.
  const cname = process.env.REACT_APP_FILESTACK_CNAME;

  if (!apiKey) {
    throw new Error(
      "REACT_APP_FILESTACK_API_KEY environment variable is not set",
    );
  }

  return filestack.init(apiKey, { ...(cname && { cname }), ...options });
};
